import {
  AutomaticGroupComponent,
  CommunicationSizeComponent,
  ComponentIds,
  Entity,
  GuestConnectionsComponent,
  HierarchyUtils,
  Vector,
  WorldTransformComponent
} from '@inter-ikea-core/core';
import { ErrorMessages, ProductInfoComponent } from '@inter-ikea-storageone/range';
import { CoreApi, Product, ProductType } from '@inter-ikea-storageone/range';
import {
  QuestionSheetState,
  setQuestionSheetVisible,
  setQuestionSheetVisibleWithProps
} from 'ducks/questionSheet';
import { BehaviorParameters } from 'rangeConfig/@types';
import { Dispatch } from 'react';
import keepOpenFramesBilly from '../images/keepOpenFramesBilly.png';
import { setKeepOpenFrames } from 'ducks/changeFronts';
import doorsUpAndDownImg from '../images/doorsUpAndDownImg.png';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import {
  addDoorsUpAndDown,
  rememberAddDoorsUpAndDown,
  setAddDoorsUpAndDown
} from '../features/AddDoorsUpAndDownFeature';

enum DoorHeights {
  H1920 = 1920,
  H970 = 970,
  H350 = 350
}

export const addDoors = ({ coreApi, props, rootState, dispatch }: BehaviorParameters) => {
  const productId = props.coreProductIds ? props.coreProductIds[0] : props.coreProductId;
  const doorProduct = rootState.products.inMarket.find(
    product => product.coreProductId == productId
  );
  // Get the mirrored left door, used if adding to wide frames
  const leftDoorProduct = rootState.products.inMarket.find(
    product =>
      product.globalItemNo === doorProduct?.globalItemNo && product.coreProductId.includes('left')
  );

  if (!doorProduct?.coreProductId) return;

  const keepOpenShelves = rootState.changeFronts.keepOpenFrames;

  const shouldAddDoorsUpAndDown = addDoorsUpAndDown(rootState) ?? false;
  const shouldRememberChoice = rememberAddDoorsUpAndDown(rootState) ?? false;

  // Get frame(s) to add doors to
  const target = coreApi.entities.findSelectedEntity();
  const entitiesInScene = coreApi.entities.getAll();
  const framesInScene = target
    ? [target]
    : entitiesInScene.filter(entity => entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID));

  // Get the door height - since functionality varies if extension door for example
  const doorHeight = doorProduct?.communicatedDimensions.height ?? 0;

  // Handle extension doors separately
  const addingExtensionDoor = doorHeight === DoorHeights.H350;
  if (addingExtensionDoor) {
    addExtensionDoors(coreApi, framesInScene, doorProduct, leftDoorProduct);
    return;
  }

  const cornerFramesInScene = framesInScene.filter(
    f =>
      f.getComponent<GuestConnectionsComponent>(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
        ?.highestPriorityConnection?.guestConnector.key === 'wall-corner'
  );

  if (cornerFramesInScene.length > 0) {
    // If corner frame, make an add that will fail, to trigger the error message
    coreApi.products.addToTarget('123', cornerFramesInScene[0]);
    coreApi.emit.onPlacementAttempt(ErrorMessages.BillyCornerFrameNoDoors);
  }

  // Filter out frames that are not suitable for the height of the door (taking into account if interior like desk is blocking)
  const relevantFrames = framesInScene.filter(frame => {
    const frameHeight =
      frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size?.height ?? 0;

    const interiorBlockingDoor =
      frameHasCollidingInterior(frame, coreApi) &&
      (frameHeight <= DoorHeights.H970 ||
        (frameHeight === DoorHeights.H1920 && doorHeight > DoorHeights.H970));

    const isCorner =
      frame.getComponent<GuestConnectionsComponent>(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
        ?.highestPriorityConnection?.guestConnector.key === 'wall-corner';

    const isExtension = coreApi.entities.isProductType(frame, ProductType.BILLY_FRAME_EXTENSION);

    return (
      isDoorHeightValidForFrame(frameHeight, doorHeight) &&
      !interiorBlockingDoor &&
      !isCorner &&
      !isExtension
    );
  });

  // Of all relevant frames, filter out the empty ones with 0 doors
  const emptyFrames = [...relevantFrames].filter(frame => {
    const doorsOnFrame = coreApi.entities
      .getFrameDescendants(frame)
      .filter(entity => entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID));

    return doorsOnFrame.length === 0;
  });

  const emptyFrameHeights = emptyFrames?.map(
    f =>
      f.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size
        ?.height
  );

  const occupiedFrameHeights = relevantFrames
    ?.filter(frame => !emptyFrames?.includes(frame))
    .map(
      f =>
        f.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size?.height
    );

  const should970DoorBeAppliedToEmptyFrame =
    doorHeight === DoorHeights.H970 &&
    emptyFrameHeights?.includes(1060) &&
    !occupiedFrameHeights?.includes(1060);

  // Determine whether to aks user to fill empty spaces with doors of empty frames in scene
  const emptyFramesInScene =
    emptyFrames.length > 0 && emptyFrames?.length !== relevantFrames?.length;

  // User gets the option to either add door to all frames, or just the one that currently has doors
  // This handles the latter (i.e. only frames with doors)
  const addDoorsToNoneEmptyFrames = () => {
    const framesToIgnore = emptyFrames.map(frame => frame.id);
    const framesToUpdate = [...relevantFrames]?.filter(f => !framesToIgnore.includes(f.id));
    const frames = framesToUpdate.length > 0 ? framesToUpdate : emptyFrames.splice(0, 1);
    const framesWithRoomForTopDoors =
      doorHeight === DoorHeights.H970
        ? framesToUpdate.filter(d => checkIsRoomForTopDoors(d, coreApi))
        : [];

    addDoorsToFrames(
      frames,
      coreApi,
      doorProduct,
      leftDoorProduct,
      dispatch,
      framesWithRoomForTopDoors,
      { addDoorsUpAndDown: shouldAddDoorsUpAndDown, rememberChoice: shouldRememberChoice }
    );
    framesWithRoomForTopDoors.length === 0 && dispatch(setQuestionSheetVisible(false));
  };

  // This handles the former (i.e. all frames)
  const addDoorsToAllFrames = () => {
    const framesWithRoomForTopDoors =
      doorHeight === DoorHeights.H970
        ? relevantFrames.filter(d => checkIsRoomForTopDoors(d, coreApi))
        : [];
    const framesToUpdate = should970DoorBeAppliedToEmptyFrame ? emptyFrames : relevantFrames;

    addDoorsToFrames(
      framesToUpdate,
      coreApi,
      doorProduct,
      leftDoorProduct,
      dispatch,
      framesWithRoomForTopDoors,
      { addDoorsUpAndDown: shouldAddDoorsUpAndDown, rememberChoice: shouldRememberChoice }
    );
    framesWithRoomForTopDoors.length === 0 && dispatch(setQuestionSheetVisible(false));
  };

  // If empty frames and no choice made, ask user to fill empty spaces with doors. Otherwise, add doors according to choice
  if (
    emptyFramesInScene &&
    typeof keepOpenShelves === 'undefined' &&
    !should970DoorBeAppliedToEmptyFrame
  ) {
    dispatch(
      setQuestionSheetVisibleWithProps({
        modalVisible: true,
        title: 'storageone.keepOpenShelvesSheetBillyTitle',
        preamble: 'storageone.keepOpenShelvesSheetBillyBody',
        primaryButtonText: 'storageone.keepOpenShelvesSheetBillyButtonPrimary',
        secondaryButtonText: 'storageone.keepOpenShelvesSheetBillyButtonSecondary',
        onAccept: () => {
          addDoorsToNoneEmptyFrames();
          dispatch(setKeepOpenFrames(true));
        },
        onDismiss: () => {
          addDoorsToAllFrames();
          dispatch(setKeepOpenFrames(false));
        },
        imageRealtivePath: keepOpenFramesBilly
      } as QuestionSheetState)
    );
  } else {
    keepOpenShelves ? addDoorsToNoneEmptyFrames() : addDoorsToAllFrames();
  }
};

// Ask user if to add doors up (if applicable), then iterate over relevant frames and add doors
function addDoorsToFrames(
  frames: Entity[],
  coreApi: CoreApi,
  doorProduct: Product | undefined,
  leftDoorProduct: Product | undefined,
  dispatch: Dispatch<any>,
  framesWithRoomForTopDoors: Entity[],
  addDoorsUpAndDown: { addDoorsUpAndDown: boolean; rememberChoice: boolean }
) {
  // If atleast one frame can accept doors on top, and no choice made, ask user to add up and down. Othwerwise, add doors according to choice
  if (framesWithRoomForTopDoors.length > 0 && !addDoorsUpAndDown.rememberChoice) {
    frames?.forEach(frame => addDoorsToFrame(frame, coreApi, doorProduct, leftDoorProduct, false));
    dispatch(
      setQuestionSheetVisibleWithProps({
        modalVisible: true,
        title: 'storageone.addDoorsUpAndDownSheetTitle',
        preamble: 'storageone.addDoorsUpAndDownSheetBody',
        primaryButtonText: 'storageone.addDoorsUpAndDownSheetButtonPrimary',
        secondaryButtonText: 'storageone.addDoorsUpAndDownSheetButtonSecondary',
        imageRealtivePath: doorsUpAndDownImg,
        onAccept: () => {
          const addDoorsUpAndDown = false;
          frames?.forEach(frame =>
            addDoorsToFrame(frame, coreApi, doorProduct, leftDoorProduct, addDoorsUpAndDown)
          );
          dispatch(setQuestionSheetVisible(false));
          dispatch(setAddDoorsUpAndDown(addDoorsUpAndDown));
        },
        onDismiss: () => {
          const addDoorsUpAndDown = true;
          framesWithRoomForTopDoors?.forEach(frame =>
            addDoorsToFrame(frame, coreApi, doorProduct, leftDoorProduct, addDoorsUpAndDown)
          );
          dispatch(setQuestionSheetVisible(false));
          dispatch(setAddDoorsUpAndDown(addDoorsUpAndDown));
        }
      } as QuestionSheetState)
    );
  } else {
    frames?.forEach(frame =>
      addDoorsToFrame(
        frame,
        coreApi,
        doorProduct,
        leftDoorProduct,
        addDoorsUpAndDown.addDoorsUpAndDown
      )
    );
  }
}

// Add doors to a frame
// Takes into account if frame is wide and needs two doors
// And if there is room for doors on top of frame
// And if interior is blocking bottom and door should be moved up
function addDoorsToFrame(
  frame: Entity,
  coreApi: CoreApi,
  doorProduct: Product | undefined,
  leftDoorProduct: Product | undefined,
  addTopDoors: boolean
) {
  const doorConnectorKey = 'attach-frame-top';

  if (!doorProduct) return;

  const frameSize = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;

  const addLeftDoor = frameSize?.width === 800;

  const rightDoor = coreApi.getServices().productManager.createEntity(doorProduct.coreProductId);
  if (!rightDoor) return;

  const { entity: bottomSnapEntity, connectorKey: bottomHoleKey } = getDoorSnapData(
    rightDoor,
    frame,
    coreApi,
    false
  );

  let placementResult;

  // Remove all exisitng doors
  deleteDoorsOnFrame(frame, coreApi, addTopDoors);

  // Add bottom right door
  if (rightDoor && bottomSnapEntity) {
    const bottomRightConnectorKey = `hole-series-right-${bottomHoleKey}`;
    placementResult = coreApi.placementService.snapToConnector(
      rightDoor,
      doorConnectorKey,
      bottomSnapEntity,
      bottomRightConnectorKey
    );
  }

  // Add bottom left door
  if (addLeftDoor && bottomSnapEntity) {
    const leftDoor =
      leftDoorProduct?.coreProductId &&
      coreApi.getServices().productManager.createEntity(leftDoorProduct?.coreProductId);

    const bottomLeftConnectorKey = `hole-series-left-${bottomHoleKey}`;
    leftDoor &&
      coreApi.placementService.snapToConnector(
        leftDoor,
        doorConnectorKey,
        bottomSnapEntity,
        bottomLeftConnectorKey
      );
  }

  if (addTopDoors) {
    const { entity: topSnapEntity, connectorKey: topHoleKey } = getDoorSnapData(
      rightDoor,
      frame,
      coreApi,
      true
    );

    const topRightDoor = coreApi
      .getServices()
      .productManager.createEntity(doorProduct.coreProductId);

    // Add top right door
    if (topRightDoor && topSnapEntity) {
      const topRightConnectorKey = `hole-series-right-${topHoleKey}`;
      coreApi.placementService.snapToConnector(
        topRightDoor,
        doorConnectorKey,
        topSnapEntity,
        topRightConnectorKey
      );
    }

    // Add top left door
    if (addLeftDoor && topSnapEntity) {
      const leftDoor =
        leftDoorProduct?.coreProductId &&
        coreApi.getServices().productManager.createEntity(leftDoorProduct?.coreProductId);

      const topLeftConnectorKey = `hole-series-left-${topHoleKey}`;
      leftDoor &&
        coreApi.placementService.snapToConnector(
          leftDoor,
          doorConnectorKey,
          topSnapEntity,
          topLeftConnectorKey
        );
    }
  }

  // Listen to placement attempts and warn user if it fails
  if (
    placementResult &&
    !placementResult.valid &&
    placementResult.errors &&
    placementResult.errors.length > 0
  ) {
    const placementAttemptErrorMessage = !hideNoFramesInSceneErrorMessage(coreApi)
      ? ErrorMessages.NoFramesInScene
      : ErrorMessages.ItemWontFit;
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
  }
}

function isDoorHeightValidForFrame(frameHeight: number, doorHeight: number) {
  if (doorHeight === DoorHeights.H1920) {
    return frameHeight === DoorHeights.H1920 + 100;
  } else if (doorHeight === DoorHeights.H970) {
    return frameHeight >= DoorHeights.H970;
  } else if (doorHeight === DoorHeights.H350) {
    return frameHeight === DoorHeights.H350;
  }
  return false;
}

function checkIsRoomForTopDoors(frame: Entity, coreApi: CoreApi) {
  const extensions = coreApi.entities.getDescendantsWithProductType(
    frame,
    ProductType.BILLY_FRAME_EXTENSION
  );
  const hasExtension = extensions?.length > 0;

  const groupMembers = getEntityGroupMembers(frame, coreApi);
  const groupHasExtension =
    groupMembers &&
    groupMembers?.filter(member =>
      coreApi.entities.getDescendantsWithProductType(member, ProductType.BILLY_FRAME_EXTENSION)
    )?.length > 0;

  const is2020Frame =
    frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
      ?.size?.height === 2020;

  const hasCollidingInterior = frameHasCollidingInterior(frame, coreApi);

  return (hasExtension || groupHasExtension) && is2020Frame && !hasCollidingInterior;
}

function frameHasCollidingInterior(frame: Entity, coreApi: CoreApi) {
  const desks = coreApi.entities.getDescendantsWithProductType(frame, ProductType.BILLY_DESK);
  const drawers = coreApi.entities.getDescendantsWithProductType(frame, ProductType.BILLY_DRAWER);
  return desks?.length > 0 || drawers?.length > 0;
}

function getEntityGroupMembers(frame: Entity, coreApi: CoreApi) {
  const parent = coreApi.entities.getParent(frame);
  const automaticGroup = parent?.getComponent<AutomaticGroupComponent>(
    ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID
  );

  const members = automaticGroup?.members;
  return members;
}

function deleteDoorsOnFrame(frame: Entity, coreApi: CoreApi, addTopDoors: boolean) {
  const groupMembers = getEntityGroupMembers(frame, coreApi);
  const doorsInGroup = groupMembers?.filter(member =>
    member.hasComponent(ComponentIds.DOOR_COMPONENT_ID)
  );

  const doorsOnFrame = coreApi.entities
    .getFrameDescendants(frame)
    .filter(entity => entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID));

  const extension = coreApi.entities.getDescendantsWithProductType(
    frame,
    ProductType.BILLY_FRAME_EXTENSION
  )?.[0];

  const doorsOnExtension = addTopDoors
    ? extension &&
      coreApi.entities
        .getFrameDescendants(extension)
        .filter(entity => entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID))
    : [];

  const doors = [...doorsOnFrame, ...(doorsInGroup ?? []), ...(doorsOnExtension ?? [])];
  doors?.forEach(door => coreApi.entities.delete(door));
}

function addExtensionDoors(
  coreApi: CoreApi,
  frames: Entity[],
  doorProduct: Product,
  leftDoorProduct?: Product
) {
  const extensionsInScene = frames.filter(e =>
    coreApi.entities.isProductType(e, ProductType.BILLY_FRAME_EXTENSION)
  );

  // Begin by deleting all 350 doors from extensions
  extensionsInScene.forEach(frame => {
    const doors = coreApi.entities
      .getFrameDescendants(frame)
      .filter(entity => entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID));
    doors.forEach(door => {
      const doorHeight =
        door.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.height ?? 0;
      if (doorHeight === DoorHeights.H350) {
        coreApi.entities.delete(door);
      }
    });
  });

  // Divide extensions into top and bottom extensions (based on #parent frames)
  const bottomExtensions: { extension: Entity; addLeftDoor: boolean }[] = [];
  const topExtensions: { extension: Entity; addLeftDoor: boolean }[] = [];
  extensionsInScene.forEach(frame => {
    const parents = HierarchyUtils.getHierarchyWithComponent(
      frame,
      ComponentIds.FRAME_COMPONENT_ID
    );
    const extension =
      parents.length === 1
        ? parents[0]
        : parents.find(
            extension =>
              !coreApi.entities.findDescendantWithOneOfProductTypes(extension, [
                ProductType.BILLY_FRAME_EXTENSION
              ])
          );
    if (!extension) return;

    const frameSize = frame.getComponent<CommunicationSizeComponent>(
      ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
    )?.size;

    // Whether to add both right and left door
    const addLeftDoor = frameSize?.width === 800;

    if (parents.length === 1) {
      topExtensions.push({ extension, addLeftDoor });
    } else {
      bottomExtensions.push({ extension, addLeftDoor });
    }
  });

  // First, add new doors to all top extensions
  topExtensions.forEach(frame => {
    coreApi.products.addToTarget(doorProduct.coreProductId, frame.extension);
    if (frame.addLeftDoor && leftDoorProduct?.coreProductId) {
      coreApi.products.addToTarget(leftDoorProduct.coreProductId, frame.extension);
    }
  });

  // Then, add new doors to all bottom extensions
  bottomExtensions.forEach(frame => {
    const extensionPosition = frame.extension.getComponent<WorldTransformComponent>(
      ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
    )?.transform.position;
    const desiredPosition = new Vector(
      extensionPosition?.X ?? 0,
      (extensionPosition?.Y ?? 0) - DoorHeights.H350,
      extensionPosition?.Z ?? 0
    );
    const rightDoorEntity = coreApi
      .getServices()
      .productManager.createEntity(doorProduct.coreProductId);

    rightDoorEntity &&
      coreApi.placementService.snapToPosition(rightDoorEntity, desiredPosition, false);

    if (frame.addLeftDoor && leftDoorProduct?.coreProductId) {
      const leftDoorEntity = coreApi
        .getServices()
        .productManager.createEntity(leftDoorProduct.coreProductId);

      leftDoorEntity &&
        coreApi.placementService.snapToPosition(leftDoorEntity, desiredPosition, false);
    }
  });
}

export function getExtensionsOnFrame(
  frame: Entity,
  coreApi: BehaviorParameters['coreApi']
): Entity[] {
  const parent = coreApi.entities.getParent(frame);
  const automaticGroup = parent?.getComponent<AutomaticGroupComponent>(
    ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID
  );
  let extensions = [];

  // To check for existing extensions we must cosider that an autmatic entity group has been created
  // This happens when a door is placed on top of both the base frame and an extension

  // If the automatic group has no members, we can check the hierarcy we normally do
  const members = automaticGroup?.members;
  if (!members) {
    // No group, can use hierarchy as is
    const extension = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(frame, [
      ProductType.BILLY_FRAME_EXTENSION
    ]);

    const secondExtension =
      extension &&
      coreApi.entities.findDescendantWithOneOfProductTypes(extension, [
        ProductType.BILLY_FRAME_EXTENSION
      ]);

    extensions = [extension, secondExtension].filter(Boolean) as Entity[];
  } else {
    // If we detect membeers, we interate through the mto find products of type extension
    extensions = members?.filter(
      member =>
        member.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
        member.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
          ProductType.BILLY_FRAME_EXTENSION
    );
  }
  return extensions;
}

function getTopExtension(extensions: Entity[]) {
  return extensions.sort((a, b) => {
    const aYPos =
      a.getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform
        ?.position?.Y ?? 0;

    const bYPos =
      b.getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform
        .position.Y ?? 0;

    return bYPos - aYPos;
  })?.[0];
}

function getDoorSnapData(
  door: Entity,
  frame: Entity,
  coreApi: CoreApi,
  addTopDoors: boolean
): { entity: Entity; connectorKey: string } {
  const is1920Door =
    door?.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
      ?.size?.height === DoorHeights.H1920;

  const extensions = getExtensionsOnFrame(frame, coreApi);
  const moveBottomDoorsUp = frameHasCollidingInterior(frame, coreApi);

  if (is1920Door) {
    // Default case, 1920 door snap to top series to cover whole frame
    let snapEntity = frame;
    let connectorKey = `front-top-${25}`;

    // if 1920 door but needs to be moved upward, snap to top extension
    if (moveBottomDoorsUp) {
      snapEntity = getTopExtension(extensions);
      connectorKey = `front-${6}`;
    }

    return { entity: snapEntity, connectorKey };
  }

  // Default case, 970 door snap to bottom series to cover bottom half of frame
  let snapEntity = frame;
  let connectorKey = `front-bottom-${25}`;

  const frameHeight = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size.height;

  if (frameHeight === 1060) {
    connectorKey = connectorKey.replace('-bottom', '');
  }

  // if 970 door but needs to be moved upward, snap to top extension
  if (moveBottomDoorsUp || addTopDoors) {
    snapEntity = getTopExtension(extensions);
    connectorKey = `front-${6}`;
  }

  return { entity: snapEntity, connectorKey };
}
